import { AlertTriangle, CheckCircle2, Forward, Info, Loader2 } from "lucide-react";
import type { ComponentType } from "react";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useCreaSollecito } from "@/hooks/useBacheca";
import { useDecisioniAnomalie } from "@/hooks/useDashboard";
import { cn } from "@/lib/utils";
import type { AnomaliaItem } from "@/types/api";

const SEVERITA_STILE: Record<
  AnomaliaItem["severita"],
  { icon: ComponentType<{ className?: string }>; colore: string }
> = {
  danger: { icon: AlertTriangle, colore: "text-red-600" },
  warning: { icon: AlertTriangle, colore: "text-amber-600" },
  info: { icon: Info, colore: "text-muted-foreground" },
};

/** Etichetta umana del responsabile per ruolo (default inoltro). */
const RESP_LABEL: Record<string, string> = {
  mp_logistica: "Marina",
  mp_management: "Fabio",
};

function nomeResponsabile(ruolo: string | null): string {
  if (!ruolo) return "responsabile";
  return RESP_LABEL[ruolo] ?? ruolo;
}

/**
 * Anomalie operative reali (carichi bloccati, oversize, paese mancante) lette da
 * `/api/v1/dashboard/decisioni-anomalie`. Da qui la Direzione può inoltrare un
 * sollecito al responsabile (crea una voce SOLLECITO in Bacheca).
 */
export function WidgetDecisioniAnomalie() {
  const { data, isLoading, isError } = useDecisioniAnomalie();

  if (isLoading) {
    return <Skeleton className="h-16 w-full" />;
  }

  if (isError) {
    return (
      <p className="text-xs text-muted-foreground">
        Impossibile caricare le anomalie al momento.
      </p>
    );
  }

  const anomalie = data?.anomalie ?? [];
  if (anomalie.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <CheckCircle2 className="size-4 text-emerald-600" />
        Nessuna anomalia rilevata. Tutto in ordine.
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {anomalie.map((a) => (
        <AnomaliaRiga key={a.tipo} anomalia={a} />
      ))}
    </ul>
  );
}

function AnomaliaRiga({ anomalia: a }: { anomalia: AnomaliaItem }) {
  const { icon: Icon, colore } = SEVERITA_STILE[a.severita] ?? SEVERITA_STILE.info;
  const [conferma, setConferma] = useState(false);
  const crea = useCreaSollecito();
  const dest = nomeResponsabile(a.destinatario_ruolo);
  const inoltrato = crea.isSuccess;

  function inoltra() {
    if (!a.destinatario_ruolo) return;
    crea.mutate({
      titolo: a.titolo,
      destinatario_ruolo: a.destinatario_ruolo,
      carico_ids: a.carico_ids,
    });
  }

  return (
    <li className="flex items-start gap-2">
      <Icon className={cn("mt-0.5 size-3.5 shrink-0", colore)} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-foreground">
          {a.titolo}
          <span className={cn("ml-1 tabular-nums", colore)}>· {a.conteggio}</span>
        </p>
        {a.hint && <p className="text-xs text-muted-foreground">{a.hint}</p>}

        {a.destinatario_ruolo && (
          <div className="mt-1.5">
            {inoltrato ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
                <CheckCircle2 className="size-3" />
                Sollecito creato in Bacheca e assegnato a {dest}
              </span>
            ) : crea.isError ? (
              <span className="text-[11px] text-red-600">
                Inoltro non riuscito. Riprova (serve un login con i permessi).
              </span>
            ) : conferma ? (
              <span className="inline-flex items-center gap-2 text-[11px]">
                <span className="text-muted-foreground">Inoltrare a {dest}?</span>
                <button
                  type="button"
                  onClick={inoltra}
                  disabled={crea.isPending}
                  className="inline-flex items-center gap-1 rounded border border-brand-300 px-2 py-0.5 font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-60"
                >
                  {crea.isPending && <Loader2 className="size-3 animate-spin" />}
                  Conferma
                </button>
                <button
                  type="button"
                  onClick={() => setConferma(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Annulla
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConferma(true)}
                className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:border-brand-300 hover:bg-accent hover:text-brand-700"
              >
                <Forward className="size-3" />
                Inoltra a {dest}
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
